/**
 * Quick DM search (sidebar Direct messages, and the Chat list header on phones): type a name, pick a person, and the
 * 1:1 DM opens (or is created) straight away, with no dialog. "New message" stays for group messages.
 * Built on the shared QuickSearch combobox (also used by the channel search).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useConversations, useMembers, useWorkspaceId } from '@/lib/hooks';
import { qk, queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { Avatar, QuickSearch } from '@/components/ui';
import { rankDmCandidates } from './dmSearch';

export function DmQuickSearch({ onClose, tone = 'dark' }: { onClose: () => void; tone?: 'dark' | 'light' }) {
  const ws = useWorkspaceId();
  const { userId } = useAuth();
  const nav = useNavigate();
  const { members } = useMembers();
  const convs = useConversations();
  const presence = useUi(s => s.presence);
  const toast = useUi(s => s.toast);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const rows = useMemo(() => rankDmCandidates(members, convs.data?.conversations ?? [], userId, q), [members, convs.data, userId, q]);
  const dark = tone === 'dark';
  type Row = (typeof rows)[number];

  const open = async (r: Row) => {
    if (!ws || busy) return;
    if (r.dmId) { onClose(); nav(`/c/${r.dmId}`); return; }
    setBusy(true);
    try {
      const c = await api.openDm(ws, [r.member.user_id]);
      void queryClient.invalidateQueries({ queryKey: qk.conversations(ws) });
      onClose();
      nav(`/c/${c.id}`);
    } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };

  return (
    <QuickSearch rows={rows} q={q} onQ={setQ} onPick={r => void open(r)} onClose={onClose} tone={tone}
      rowKey={r => r.member.user_id} label="Find a person to message" listLabel="People" placeholder="Find a person…"
      emptyText={t => <>No one matches “{t}”</>} testId="dm-quick-search" resultTestId="dm-quick-result"
      renderRow={r => (<>
        <Avatar size={22} name={r.member.display_name} id={r.member.user_id} presence={presence[r.member.user_id] ?? 'offline'} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-medium">{r.member.display_name}</span>
          {r.member.job_title && <span className={clsx('block truncate text-[11px]', dark ? 'text-sidebar-muted' : 'text-slate-500')}>{r.member.job_title}</span>}
        </span>
        {r.dmId && <span className={clsx('shrink-0 text-[10px] uppercase tracking-wide', dark ? 'text-sidebar-muted' : 'text-slate-500')}>Chat</span>}
      </>)} />
  );
}
